import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  Col,
  Flex,
  FormContainer,
  Icon,
  Row,
  Switch,
  Text,
  Title,
  notify,
} from '@wyz/ui';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useState } from 'react';
import {
  CriteriaBuilder,
  MatchedCustomersTable,
  SelectField,
  TextField,
} from '~/common/components';
import { FEATURE_CONTEXT } from '~/common/schemas';
import { Trans, useI18n } from '~/common/context';
import {
  S2S_FLOW_TYPES,
  buildS2sFlowTemplateFormSchema,
} from '~/connect/schemas';
import {
  IS2SFlow,
  IS2SFlowTemplateForm,
  IS2SFlowTemplatePayload,
  IS2SBusinessRuleFormEntry,
  IS2SBusinessRuleLookup,
} from '~/connect/types';
import { ValidationErrorResponse } from '@wyz/types';
import { Link } from 'react-router';
import { useManageCriteria } from '~/common/api';
import { useBackTo, useValidationForm } from '~/common/hooks';
import { CommonErrorCodesEnumType } from '~/common/types';
import { useWyzFlow } from '~/connect/api';
import { S2SFlowTemplateHelpers } from '~/connect/helpers';
import { TranslationKeys } from '~/common/types';
import {
  BusinessRuleParameters,
  BusinessRulesModal,
} from '~/connect/components';

type Props =
  | {
      defaultValues?: Partial<IS2SFlowTemplateForm>;
      mode?: 'CREATE' | 'UPDATE';
      isLoading: boolean;
      onSubmit: (data: IS2SFlowTemplatePayload) => void;
      errors?: ValidationErrorResponse<CommonErrorCodesEnumType> | null;
      businessRules: IS2SBusinessRuleLookup;
    }
  | {
      mode?: 'READ_ONLY';
      defaultValues?: Partial<IS2SFlowTemplateForm>;
      isLoading?: never;
      onSubmit?: never;
      errors?: never;
      businessRules: IS2SBusinessRuleLookup;
    };

export function S2SFlowTemplateForm({
  mode = 'CREATE',
  defaultValues = S2SFlowTemplateHelpers.S2S_FLOW_TEMPLATE_FORM_DEFAULT_VALUES,
  isLoading,
  onSubmit,
  errors,
  businessRules,
}: Props) {
  const backTo = useBackTo('..');
  const { t } = useI18n();

  const resolver = zodResolver(
    buildS2sFlowTemplateFormSchema(businessRules, t),
  );
  const methods = useForm<IS2SFlowTemplateForm>({
    defaultValues,
    resolver,
  });
  const formErrors = methods.formState.errors;

  useValidationForm({
    methods,
    errors: errors?.errors,
    t,
  });

  const manageCriteria = useManageCriteria({ getAll: { enabled: true } });

  const wyzFlow = useWyzFlow({
    partners: { enabled: true },
    flowTypes: { enabled: true },
  });

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'flows',
  });

  const watchedFlows = useWatch({ control: methods.control, name: 'flows' });
  const watchedBusinessRules = useWatch({
    control: methods.control,
    name: 'businessRules',
  });

  const [isBusinessRulesModalOpen, setIsBusinessRulesModalOpen] =
    useState(false);

  const newFlowType =
    useWatch({ control: methods.control, name: 'newFlowType' }) ?? '';
  const newFlowPartner =
    useWatch({ control: methods.control, name: 'newFlowPartner' }) ?? '';

  const flowTypeOptions =
    wyzFlow.flowTypes.data?.member
      ?.filter((endpoint) =>
        (S2S_FLOW_TYPES as readonly string[]).includes(endpoint.flow_type),
      )
      .map((endpoint) => ({
        value: endpoint.flow_type,
        label: t(
          `app.s2s_flow_template.flow_type.${endpoint.flow_type}` as keyof TranslationKeys,
        ),
      })) ?? [];

  const partnerOptions =
    wyzFlow.partners?.data?.member
      ?.filter((p) => p.name !== 'NO')
      .map((p) => ({
        value: p.name,
        label: p.name,
      })) ?? [];

  const handleAddFlow = () => {
    if (!newFlowType) return;

    const isDuplicate = fields.some(
      (flow) =>
        flow.type === newFlowType &&
        (flow.partner ?? '') === (newFlowPartner || ''),
    );

    if (isDuplicate) {
      notify.error(
        <Trans i18nKey='app.s2s_flow_template.form.flows.duplicate_error' />,
        { id: 'toast-error-duplicate-flow' },
      );
      return;
    }

    const endpoint = wyzFlow.flowTypes.data?.member?.find(
      (e) => e.flow_type === newFlowType,
    );

    append({
      type: newFlowType as IS2SFlow['type'],
      partner: newFlowPartner || null,
      status: 'INACTIVE',
      url: endpoint?.path ?? null,
    });

    methods.setValue('newFlowType', '');
    methods.setValue('newFlowPartner', '');
  };

  return (
    <FormContainer
      methods={methods}
      submitHandler={(data) => {
        onSubmit?.(
          S2SFlowTemplateHelpers.fromFormToPayload(
            data,
            manageCriteria.getAll.data?.data ?? [],
          ),
        );
      }}
      formConfiguration={{
        mode: mode === 'READ_ONLY' ? 'READ_ONLY' : 'WRITE',
      }}
    >
      <Card className='mb-section'>
        <Card.Header>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='post' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.common.general_information' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xs='3'>
              <TextField
                required
                name='name'
                label={<Trans i18nKey='app.common.model_name' />}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className='mb-section'>
        <Card.Header className='pb-0'>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='clip-board' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.common.criteria' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <CriteriaBuilder name='criteria'>
            <Row className='mt-4'>
              <Col md={6}>
                <MatchedCustomersTable
                  featureContext={FEATURE_CONTEXT.S2S_FLOW_TEMPLATE}
                  name='criteria'
                />
              </Col>
            </Row>
          </CriteriaBuilder>
        </Card.Body>
      </Card>

      <Card className='mb-section'>
        <Card.Header className='pb-0'>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='settings' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.s2s_flow_template.form.flows.title' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          {mode !== 'READ_ONLY' && (
            <>
              <Text
                heading='normal'
                weight='bold'
                color='tertiary'
                className='mb-3'
              >
                <Trans i18nKey='app.s2s_flow_template.form.flows.add_new' />
              </Text>
              <Row className='mb-4 align-items-end'>
                <Col md={3}>
                  <SelectField<false>
                    name='newFlowType'
                    label={
                      <Trans i18nKey='app.s2s_flow_template.form.flows.type' />
                    }
                    options={flowTypeOptions}
                  />
                </Col>
                <Col md={3}>
                  <SelectField<false>
                    name='newFlowPartner'
                    label={
                      <Trans i18nKey='app.s2s_flow_template.form.flows.partner' />
                    }
                    options={partnerOptions}
                  />
                </Col>
                <Col md={2} className='pb-2'>
                  <Button
                    id='add-flow'
                    variant='primary'
                    type='button'
                    onClick={handleAddFlow}
                    disabled={!newFlowType}
                  >
                    <Icon icon='plus' width='1rem' />{' '}
                    <Trans i18nKey='app.s2s_flow_template.form.flows.add' />
                  </Button>
                </Col>
              </Row>
            </>
          )}

          {fields.length > 0 && (
            <>
              <Text
                heading='normal'
                weight='bold'
                color='tertiary'
                className='mb-3'
              >
                <Trans
                  i18nKey='app.s2s_flow_template.form.flows.added_count'
                  values={{ count: fields.length }}
                />
              </Text>
              <div className='d-flex flex-column gap-3'>
                {fields.map((field, index) => (
                  <Card
                    key={field.id}
                    className={`shadow-none border ${
                      watchedFlows?.[index]?.status === 'ACTIVE'
                        ? 'bg-light'
                        : 'bg-white'
                    }`}
                  >
                    <Card.Header className='py-2 bg-transparent border-bottom'>
                      <Flex justifyContent='between' alignItems='center'>
                        <Text heading='normal' weight='bold' color='primary'>
                          {t(
                            `app.s2s_flow_template.flow_type.${field.type}` as keyof TranslationKeys,
                          )}
                        </Text>
                        <Flex alignItems='center' className='gap-3'>
                          <Flex alignItems='center' className='gap-2'>
                            <Text heading='sm' weight='semi-bold'>
                              <Trans i18nKey='app.s2s_flow_template.form.flows.inactive' />
                            </Text>
                            <Switch
                              id={`flow-status-${index}`}
                              checked={
                                watchedFlows?.[index]?.status === 'ACTIVE'
                              }
                              onChange={(e) =>
                                methods.setValue(
                                  `flows.${index}.status`,
                                  e.target.checked ? 'ACTIVE' : 'INACTIVE',
                                )
                              }
                              disabled={mode === 'READ_ONLY'}
                            />
                            <Text heading='sm' weight='semi-bold'>
                              <Trans i18nKey='app.s2s_flow_template.form.flows.active' />
                            </Text>
                          </Flex>
                          {mode !== 'READ_ONLY' && (
                            <Button
                              id={`delete-flow-${index}`}
                              variant='link'
                              size='sm'
                              type='button'
                              className='text-dark p-0'
                              onClick={() => remove(index)}
                            >
                              <Icon icon='trash' size='sm' />
                            </Button>
                          )}
                        </Flex>
                      </Flex>
                    </Card.Header>
                    <Card.Body className='py-2'>
                      <Flex alignItems='center' className='gap-4'>
                        {field.partner && field.partner !== 'NO' && (
                          <Text heading='sm' color='secondary'>
                            <Trans i18nKey='app.s2s_flow_template.form.flows.partner' />
                            {' : '}
                            {field.partner}
                          </Text>
                        )}
                        <Text heading='sm' color='secondary'>
                          <Trans i18nKey='app.s2s_flow_template.form.flows.url' />
                          {' : '}
                          {field.url ?? ''}
                        </Text>
                      </Flex>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Card className='mb-section'>
        <Card.Header className='pb-0'>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='clip-board' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.s2s_flow_template.form.business_rules.title' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <Flex justifyContent='between' alignItems='center'>
            <Text heading='normal' weight='semi-bold' color='secondary'>
              <Trans
                i18nKey='app.s2s_flow_template.form.business_rules.activated_count'
                values={{
                  count: watchedBusinessRules.length,
                }}
              />
            </Text>
            {mode !== 'READ_ONLY' && (
              <Button
                id='open-business-rules-modal'
                variant='link'
                type='button'
                onClick={() => setIsBusinessRulesModalOpen(true)}
              >
                <Icon icon='plus' width='1rem' />
                <Trans i18nKey='app.s2s_flow_template.form.business_rules.add' />
              </Button>
            )}
          </Flex>
          {watchedBusinessRules.length > 0 && (
            <div className='d-flex flex-column gap-2 mt-3'>
              {watchedBusinessRules.map(({ id, parameters }, index) => {
                const rule = businessRules[id];
                if (!rule) return null;

                const ruleErrors =
                  formErrors?.businessRules?.[index]?.parameters;
                return (
                  <Flex
                    key={rule.id}
                    justifyContent='between'
                    alignItems='start'
                    className='p-3 border rounded bg-light'
                  >
                    <div>
                      <div className='space-x-2'>
                        <Flex direction='column'>
                          <Text
                            component='span'
                            heading='normal'
                            weight='semi-bold'
                          >
                            <Trans i18nKey={rule.name.i18nKey} />
                          </Text>
                          <Text component='span' heading='sm' weight='light'>
                            <Trans i18nKey={rule.description.i18nKey} />
                          </Text>
                        </Flex>
                      </div>
                      {rule.parameterDefinitions.length > 0 && (
                        <BusinessRuleParameters
                          mode='readonly'
                          definitions={rule.parameterDefinitions}
                          values={parameters}
                          ruleErrors={ruleErrors}
                        />
                      )}
                    </div>
                    {mode !== 'READ_ONLY' && (
                      <Button
                        id={`remove-rule-${rule.id}`}
                        variant='secondary'
                        size='sm'
                        type='button'
                        onClick={() =>
                          methods.setValue(
                            'businessRules',
                            watchedBusinessRules.filter((r) => r.id !== id),
                          )
                        }
                      >
                        <Icon icon='minus' width='1rem' />
                        <Trans i18nKey='app.common.buttons.remove' />
                      </Button>
                    )}
                  </Flex>
                );
              })}
            </div>
          )}
        </Card.Body>
      </Card>

      <BusinessRulesModal
        open={isBusinessRulesModalOpen}
        onClose={() => setIsBusinessRulesModalOpen(false)}
        selectedRules={watchedBusinessRules}
        onConfirm={(rules: IS2SBusinessRuleFormEntry[]) =>
          methods.setValue('businessRules', rules)
        }
      />

      {mode !== 'READ_ONLY' && (
        <Card className='mb-section'>
          <Card.Body>
            <Flex className='space-x-4' justifyContent='between'>
              <Button
                id='cancel'
                as={Link}
                variant='secondary'
                type='button'
                to={backTo}
              >
                <Trans i18nKey='app.common.button.cancel' />
              </Button>
              <Button
                id='submit'
                variant='primary'
                type='submit'
                disabled={isLoading}
              >
                <Trans
                  i18nKey={
                    mode === 'CREATE'
                      ? 'app.s2s_flow_template.form.buttons.create'
                      : 'app.s2s_flow_template.form.buttons.update'
                  }
                />
              </Button>
            </Flex>
          </Card.Body>
        </Card>
      )}
    </FormContainer>
  );
}
